import { useRef, useState, useEffect } from "react";
import jsonConfig from "../Config.json";
import { BlobServiceClient } from "@azure/storage-blob";
import { useLocation, useNavigate } from "react-router-dom";
import createWebRTCConnection from "../apis/candidatescreening/createwebrtcconnection";
import getToken from "../apis/candidatescreening/gettokenbyid";
import interviewanalysis from "../apis/candidatescreening/interviewanalysis";
import createInterviewUser from "../apis/candidatescreening/createinterviewuser";
import SHA256 from 'crypto-js/sha256';
import screeningstatusupdate from "../apis/candidatescreening/screeningstatusupdate";
import assessmentprofile from "../apis/vettest/assessmentprofile";
import updatevetcandidatetestbyid from "../apis/vettest/vetmanagecandidatetestbyid";
import updateUserInterviewDetails from "../apis/candidatescreening/updateuserinterviewdetails";

const AZURE_RESOURCE = jsonConfig.azureResource;
const WEBRTC_URL = jsonConfig.webRtcUrl;

function createTranscript(role, text) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    role,
    text,
    timestamp: new Date().toLocaleTimeString(),
  };
}

function getSupportedVideoRecordingProfile() {
  const profiles = [
    { mimeType: "video/webm;codecs=vp9,opus", fileExtension: "webm" },
    { mimeType: "video/webm;codecs=vp8,opus", fileExtension: "webm" },
    { mimeType: "video/webm", fileExtension: "webm" },
    { mimeType: "video/mp4", fileExtension: "mp4" },
  ];

  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return profiles[2];
  }

  return (
    profiles.find((profile) => MediaRecorder.isTypeSupported(profile.mimeType)) ||
    profiles[2]
  );
}

export function useInterview(previewStreamRef) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    jdId,
    name,
    email,
    assessmentDurationInSeconds,
    userLocation,
    phoneNumber,
    countryCallingCode,
    isVet
  } = state || {};
  const normalizedData = {
  phoneNumber: phoneNumber,
  countryCode: userLocation?.code || "",
  countryCallingCode: (countryCallingCode || userLocation?.dialCode || "").toString().trim()
};
  const [userId, setUserId] = useState(null);
  const userIdRef = useRef(null);


  const [isRunning, setIsRunning] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [status, setStatus] = useState("Idle");
  const endInterviewRef = useRef(false);
  const [transcripts, setTranscripts] = useState([]);
  const [timer, setTimer] = useState(0);
  const recordingProfile = getSupportedVideoRecordingProfile();
  const timerValueRef = useRef(0);
  const timerRef = useRef(null);
  const segmentsArrRef = useRef([]);

  const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);
  const [isFinalizingInterview, setIsFinalizingInterview] = useState(false);
  const [isAliSpeaking, setIsAliSpeaking] = useState(false);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const recorderRef = useRef(null);

  const audioContextRef = useRef(null);
  const mixedAudioDestRef = useRef(null);

  const transcriptAppendClient = useRef(null);
  const segmentSeqRef = useRef(0);
  const pendingTranscriptionRef = useRef(null);
  let session_id = null;
  const candidateSilenceTimerRef = useRef(null);
  const consecutiveSilenceCountRef = useRef(0);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(true);
  const micAudioTrackRef = useRef(null);

  const createUUID = () => {
    let dt = new Date().getTime();
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (dt + Math.random() * 16) % 16 | 0;
        dt >>>= 4;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
    return uuid;
  };

  const hashUUID = (uuid) => {
    return SHA256(uuid).toString().substring(0, 15);
  };

  const uploadAudioToBlob = async (options) => {
    try {
      const {
        audioBlob,
        userId,
        containerName = "vetinterview",
        subfolder = "audio",
        questionNumber = null,
        recordingTime = null,
        contentType = "audio/mp3",
        fileExtension = "mp3"
      } = options;

      if (!audioBlob) throw new Error("Audio blob is required");
      if (!userId) throw new Error("User ID is required");

      const blobServiceClient = new BlobServiceClient(
        `https://${jsonConfig.blobStorageAccountName}.blob.core.windows.net${jsonConfig.sasToken}`
      );

      const containerClient = blobServiceClient.getContainerClient(containerName);
      const uuid = createUUID();
      const hashUuid = hashUUID(uuid);
      const blobName = `${userId}/${subfolder}/${hashUuid}.${fileExtension}`;
      const blobClient = containerClient.getBlockBlobClient(blobName);

      const metadata = {};
      if (questionNumber !== null) metadata.questionNumber = questionNumber.toString();
      if (recordingTime !== null) metadata.duration = recordingTime.toString();

      const blobUploadOptions = {
        blobHTTPHeaders: { blobContentType: contentType },
        metadata: metadata,
      };

      await blobClient.uploadBrowserData(audioBlob, blobUploadOptions);
      return `${hashUuid}.${fileExtension}`;
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    return () => {
      if (pcRef.current || dcRef.current) {
        stopInterview({ shouldAnalyze: false });
      }
    };
  }, []);

  const addTranscript = (role, text) => {
    setTranscripts((prev) => [...prev, createTranscript(role, text)]);
  };

  const startChunkedVideoUpload = async (options) => {
    try {
      const {
        stream,
        userId,
        containerName = "screeninginterview",
        shouldMixAudio = false,
        isVet = false
      } = options;

      if (!stream) throw new Error("Video stream not available");


      const blobPath = `${userId}/video/${userId}.${recordingProfile.fileExtension}`;
      const blobServiceClient = new BlobServiceClient(
        `https://${jsonConfig.blobStorageAccountName}.blob.core.windows.net${jsonConfig.sasToken}`
      );

      const containerClient = blobServiceClient.getContainerClient(containerName);
      const appendBlobClient = containerClient.getAppendBlobClient(blobPath);
      await appendBlobClient.create({
        blobHTTPHeaders: {
          blobContentType: recordingProfile.mimeType,
        },
      });
      if (!isVet) {
        await updateUserInterviewDetails(userId, jdId, { extension: recordingProfile?.fileExtension });
      }

      let streamToRecord = stream;

      if (shouldMixAudio) {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
          mixedAudioDestRef.current = audioContextRef.current.createMediaStreamDestination();
        }

        const audioCtx = audioContextRef.current;
        const destination = mixedAudioDestRef.current;

        const micSource = audioCtx.createMediaStreamSource(stream);
        micSource.connect(destination);

        streamToRecord = new MediaStream([
          stream.getVideoTracks()[0],
          destination.stream.getAudioTracks()[0],
        ]);
      }

      recorderRef.current = new MediaRecorder(streamToRecord, {
        mimeType: recordingProfile.mimeType,
      });

      recorderRef.current._appendInFlight = Promise.resolve();
      recorderRef.current._hasChunkUploadError = false;
      recorderRef.current._blobPath = blobPath;
      recorderRef.current._fileExtension = recordingProfile.fileExtension;

      recorderRef.current.ondataavailable = async (event) => {
        if (event.data && event.data.size > 0) {
          recorderRef.current._appendInFlight = recorderRef.current._appendInFlight
            .then(() => appendBlobClient.appendBlock(event.data, event.data.size))
            .catch((error) => {
              recorderRef.current._hasChunkUploadError = true;
              console.error("Failed to append a video chunk:", error);
            });
        }
      };

      recorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };

      recorderRef.current.start(5000);
    } catch (err) {
      console.error('Error starting video upload:', err);
    }
  };

  const initTranscriptFile = async (userId) => {
    const blobPath = `${userId}/transcript/${userId}.txt`;
    const blobaccount = jsonConfig.blobStorageAccountName;
    const sas = jsonConfig.sasToken;
    const bsClient = new BlobServiceClient(`https://${blobaccount}.blob.core.windows.net${sas}`);
    const containerClient = bsClient.getContainerClient("screeninginterview");
    transcriptAppendClient.current = containerClient.getAppendBlobClient(blobPath);

    try {
      await transcriptAppendClient.current.create();
      const header = `{
        "sessionId": "${session_id}",
        "candidateScreeningId": "${userId}",
        "language": "en-IN",
        "createdAt": "${new Date().toISOString()}",
        "engine": {
          "version": "v3.2",
          "model": "${jsonConfig.azureOpenAIModelName}"
        },
        "segments": [
      `;
      const data = new TextEncoder().encode(header);
      await transcriptAppendClient.current.appendBlock(data, data.length);
    } catch (err) {
      if (err.statusCode !== 409) { /* ignore exists */ }
    }
  };

  const appendTranscriptLine = async (speaker, message, userId) => {
    if (!transcriptAppendClient.current) return;
    const currentSegmentId = ++segmentSeqRef.current;
    const segment = {
      segmentId: currentSegmentId,
      seq: currentSegmentId,
      speaker: speaker,
      text: message,
      currentDurationSec: timerValueRef.current,
    };
    const json = JSON.stringify(segment) + ",\n";
    const data = new TextEncoder().encode(json);
    await transcriptAppendClient.current.appendBlock(data, data.length);
    segmentsArrRef.current.push(segment);
  };

  const closeTranscript = (currentTimer) => {
    let currentTime = timerValueRef.current;
    let currentSegmentId = ++segmentSeqRef.current;
    let thankyounote = "Thank you for your time, this interview will now end, we will be in touch for next steps."
    const footer = `{"segmentId":${currentSegmentId},"seq":${currentSegmentId},"speaker":"interviewer","text":"${thankyounote}","currentDurationSec":${currentTime}}
  ],
  "durationSec": ${currentTimer}
}
`;
    const data = new TextEncoder().encode(footer);
    transcriptAppendClient?.current?.appendBlock(data, data.length);
  };

  const disableMicrophone = () => {
    if (micAudioTrackRef.current) micAudioTrackRef.current.enabled = false;
    const audioTracks = previewStreamRef.current?.getAudioTracks() || [];
    audioTracks.forEach((track) => { track.enabled = false; });
    setIsMicrophoneEnabled(false);
  };

  const enableMicrophone = () => {
    if (micAudioTrackRef.current) micAudioTrackRef.current.enabled = true;
    const audioTracks = previewStreamRef.current?.getAudioTracks() || [];
    audioTracks.forEach((track) => { track.enabled = true; });
    setIsMicrophoneEnabled(true);
  };

  const startInterview = async (options = {}) => {
    const {
      stream = previewStreamRef?.current, 
      userId: passedUserId = null,
      containerName = null,
      isVet =  false,
      shouldMixAudio = true
    } = options;

    let effectiveUserId = passedUserId;

    try {
      setIsConnecting(true);
      setStatus("Starting interview...");

      if (!effectiveUserId) {
        if (!isVet) {
          const { data: userInfo } = await createInterviewUser({
            name,
            email: email.trim().toLowerCase(),
            phoneNumber: normalizedData.phoneNumber,
            countryCode: normalizedData.countryCode,
            countryCallingCode: normalizedData.countryCallingCode
          }, jdId);
          effectiveUserId = userInfo.id;
          setUserId(effectiveUserId);
          userIdRef.current = effectiveUserId;
        } else {
          
            throw new Error("User ID is required for VET interview");
          
        }
      } else {
        setUserId(effectiveUserId);
        userIdRef.current = effectiveUserId;
      }

      if (!stream) throw new Error("Video stream not available");

      await startChunkedVideoUpload({
        stream: stream,
        userId: effectiveUserId,
        containerName: containerName || (isVet ? "vetinterview" : "screeninginterview"),
        shouldMixAudio: shouldMixAudio,
        isVet: isVet
      });

      timerRef.current = setInterval(() => {
        timerValueRef.current += 1;
        setTimer(timerValueRef.current);
        if (!isVet && timerValueRef.current >= assessmentDurationInSeconds) {
          endInterviewRef.current = true;
        } else if (!isVet && timerValueRef.current >= (assessmentDurationInSeconds + 10)) {
          clearInterval(timerRef.current);
          stopInterview({ shouldAnalyze: true });
        }
      }, 1000);

      if (!isVet) {
        await initTranscriptFile(effectiveUserId);
        await screeningstatusupdate({ csId: effectiveUserId, statuscode: 'initiated' });
        setStatus("Creating WebRTC connection…");
        disableMicrophone();

        const { data } = await getToken(jdId);
        const { token, sessionId } = data;
        session_id = sessionId;

        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        if (!remoteAudioRef.current) {
          remoteAudioRef.current = new Audio();
          remoteAudioRef.current.autoplay = true;
        }

        pc.ontrack = (event) => {
          if (event.streams?.[0] && remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = event.streams[0];
            if (audioContextRef.current && mixedAudioDestRef.current && event.track.kind === "audio") {
              const assistantSource = audioContextRef.current.createMediaStreamSource(event.streams[0]);
              assistantSource.connect(mixedAudioDestRef.current);
            }
          }
        };

        pc.onconnectionstatechange = () => { setStatus(`Connection: ${pc.connectionState}`); };
        pc.oniceconnectionstatechange = () => { setStatus(`ICE: ${pc.iceConnectionState}`); };
        pc.onicecandidate = (e) => { if (e.candidate) { } };

        const audioTrack = previewStreamRef.current?.getAudioTracks()[0];
        if (!audioTrack) throw new Error("No audio track available from camera stream");
        micAudioTrackRef.current = audioTrack;
        pc.addTrack(audioTrack);

        const dc = pc.createDataChannel("events");
        dcRef.current = dc;

        dc.onopen = () => {
          setStatus("Interview in progress...");
          dc.send(JSON.stringify({
            type: "conversation.item.create",
            item: { type: "message", role: "system", content: [{ type: "input_text", text: "Follow interview flow strictly." }] }
          }));
          dc.send(JSON.stringify({
            type: "conversation.item.create",
            item: { type: "message", role: "user", content: [{ type: "input_text", text: "You may begin the interview now." }] }
          }));
          dc.send(JSON.stringify({ type: "response.create" }));
        };
        dc.onerror = (err) => { };
        dc.onclose = () => { };

        dc.onmessage = async (event) => {
          const msg = JSON.parse(event.data);
          const output = msg?.response?.output[0];
          const transcript = output?.content?.[0]?.transcript;

          if (msg.type === "output_audio_buffer.started") {
            setIsAliSpeaking(true);
            disableMicrophone();
          } else if (msg.type === "output_audio_buffer.stopped") {
            setIsAliSpeaking(false);
            enableMicrophone();
            clearTimeout(candidateSilenceTimerRef.current);
            candidateSilenceTimerRef.current = setTimeout(() => {
              if (!dc || dc.readyState !== "open") return;
              consecutiveSilenceCountRef.current += 1;
              let silenceInstruction = "";
              if (consecutiveSilenceCountRef.current === 1) {
                silenceInstruction = "[SYSTEM EVENT: First consecutive silence. The candidate has not responded. Politely encourage them to answer the current question. Do not ask a new question.]";
              } else if (consecutiveSilenceCountRef.current === 2) {
                silenceInstruction = "[SYSTEM EVENT: Second consecutive silence. Repeat or briefly clarify the current question once. Do not ask a new question yet.]";
              } else {
                silenceInstruction = "[SYSTEM EVENT: Third consecutive silence. Do not repeat the current question. Say 'Let us move to the next question' and immediately ask a completely new interview question.]";
                consecutiveSilenceCountRef.current = 0;
              }
              dc.send(JSON.stringify({
                type: "conversation.item.create",
                item: { type: "message", role: "user", content: [{ type: "input_text", text: silenceInstruction }] }
              }));
              dc.send(JSON.stringify({ type: "response.create" }));
            }, 5000);
          }

          if (msg.type === "input_audio_buffer.speech_started") {
            clearTimeout(candidateSilenceTimerRef.current);
            candidateSilenceTimerRef.current = null;
            consecutiveSilenceCountRef.current = 0;
          }

          if (msg.type === "input_audio_buffer.committed" ||
            msg.type === "conversation.item.input_audio_transcription.started") {
            let resolveFunc;
            const promise = new Promise((resolve) => { resolveFunc = resolve; });
            pendingTranscriptionRef.current = { promise: promise, resolve: resolveFunc };
          }

          if (msg.type === "conversation.item.input_audio_transcription.completed") {
            addTranscript("user", msg.transcript);
            await appendTranscriptLine("candidate", msg.transcript, userIdRef.current);
            if (pendingTranscriptionRef.current?.resolve) {
              pendingTranscriptionRef.current.resolve();
              pendingTranscriptionRef.current = null;
            }
            if (endInterviewRef.current) {
              stopInterview({ shouldAnalyze: true });
            }
          }

          if (msg.type === "response.done" && output && transcript && output?.role === "assistant") {
            if (pendingTranscriptionRef.current?.promise) {
              try { await pendingTranscriptionRef.current.promise; } catch (error) { }
            }
            addTranscript("assistant", transcript);
            await appendTranscriptLine("interviewer", transcript, userIdRef.current);
            if (transcript.toLowerCase().includes("the interview is now over")) {
              stopInterview({ shouldAnalyze: true });
            }
          }
        };

        if (!WEBRTC_URL) throw new Error("WEBRTC_URL is not configured");
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const sdpResponse = await createWebRTCConnection(WEBRTC_URL, offer.sdp, token);
        const answerSdp = sdpResponse.data;
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

        setIsRunning(true);
      } else {
        setIsRunning(true);
        setStatus("VET Interview in progress...");
        await updatevetcandidatetestbyid({ id: effectiveUserId, extension: recordingProfile?.fileExtension });
      }

    } catch (err) {
      setStatus(`Error: ${err.message}`);
      stopInterview({ shouldAnalyze: false, isVet: isVet, userId: effectiveUserId, containerName: containerName });
    } finally {
      setIsConnecting(false);
    }
  };

  const stopChunkedVideoUpload = async (options = {}) => {
    try {
      const {
        isVet = false,
        currentTimer = null,
        shouldAnalyze = false,
        userId = null,
        containerName = null
      } = options;

      const recorder = recorderRef.current;

      if (!recorder || recorder.state === "inactive") {
        return timerValueRef.current;
      }

      await new Promise((resolve) => {
        recorder.addEventListener(
          "stop",
          () => {
            resolve();
          },
          { once: true }
        );

        try {
          if (recorder.state === "recording") {
            recorder.requestData();
          }
        } catch (error) {
          console.error("Failed to request final recorder data:", error);
        }

        recorder.stop();
      });

      const actualDuration = timerValueRef.current;

      if (recorder._appendInFlight) {
        await recorder._appendInFlight;
      }

      if (recorder._hasChunkUploadError) {
        throw new Error("One or more video chunks failed to upload");
      }

      if (recorder.stream) {
        recorder.stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (isVet && previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        previewStreamRef.current = null;
      }

      if (!isVet && audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
        mixedAudioDestRef.current = null;
      }

      recorderRef.current = null;

      return actualDuration;
    } catch (err) {
      console.error('Error stopping video upload:', err);
      return 0;
    }
  };

  const playEndSound = () => {
    return new Promise((resolve) => {
      try {
        const audio = new Audio("/audio/endInterview.mp3");
        audio.volume = 1.0;

        if (audioContextRef.current && mixedAudioDestRef.current) {

          if (audioContextRef.current.state === "suspended") {
            audioContextRef.current.resume();
          }

          const source = audioContextRef.current.createMediaElementSource(audio);

          source.connect(mixedAudioDestRef.current);
          source.connect(audioContextRef.current.destination);
        }

        audio.onended = () => resolve();
        audio.onerror = () => resolve();

        audio.play().catch((err) => {
          console.warn("End sound play blocked:", err);
          resolve();
        });
      } catch (error) {
        resolve();
      }
    });
  };

  const stopInterview = async (options = {}) => {
    const {
      shouldAnalyze = false,
      isVet = false,
      userId = null,
      containerName = null
    } = options;

    if (endInterviewRef.current === "end") return;
    endInterviewRef.current = "end";

    

    clearTimeout(candidateSilenceTimerRef.current);
    candidateSilenceTimerRef.current = null;
    consecutiveSilenceCountRef.current = 0;

    dcRef.current?.close();
    pcRef.current?.close();
    dcRef.current = null;
    pcRef.current = null;

    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
    }

    if (!isVet) {
      previewStreamRef.current?.getAudioTracks().forEach(track => { track.enabled = false; });
      if (micAudioTrackRef.current) { micAudioTrackRef.current.enabled = false; }
    }

    addTranscript("assistant", "Thank you! for your time, this interview will now end. We will be in touch for the next steps.");




    if (!isVet) {
      await playEndSound();
    }

    const actualDuration = await stopChunkedVideoUpload({
      isVet: isVet,
      userId: userId || userIdRef.current,
      containerName: containerName || (isVet ? "vetinterview" : "screeninginterview"),
    });

    clearInterval(timerRef.current);
    timerRef.current = null;

    if (isVet && previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((track) => { track.stop(); });
      previewStreamRef.current = null;
    }

    if (!isVet && shouldAnalyze) {
      setIsFinalizingInterview(true);
      await Promise.allSettled([
        interviewanalysis({
          userid: userId || userIdRef.current,
          segments: segmentsArrRef.current,
          duration: actualDuration
        }),
        assessmentprofile({
          "id": userId || userIdRef.current,
          "assessmentType": "ai",
          "userLocation": userLocation,
          "phoneNumber": normalizedData.phoneNumber,
          "countryCode": normalizedData.countryCode,
          "countryCallingCode": normalizedData.countryCallingCode
        })
      ]);
      setIsFinalizingInterview(false);
    }

    
    closeTranscript(actualDuration);
    setIsRunning(false);
    setStatus("Idle");
    if (!isVet) {
      setIsInterviewCompleted(true);
    }
    return actualDuration;
  };

  return {
    isRunning,
    isConnecting,
    status,
    transcripts,
    startInterview,
    isInterviewCompleted,
    isFinalizingInterview,
    timer,
    isMicrophoneEnabled,
    userId: userId,
    jdId,
    isAliSpeaking,
    stopInterview,
    startChunkedVideoUpload,
    stopChunkedVideoUpload,
    uploadAudioToBlob
  };
}